import { FilterMatchMode } from "primereact/api";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Toast } from "primereact/toast";
import { getAllproductsService } from "../../services/Product/ProductServices";
import { showError, showSuccess } from "../Toast/Toast.functions";
import AdminPageHeader from "./AdminPageHeader";

interface NamedEntity {
  _id: string;
  name: string;
}

interface AdminNamedEntityListProps {
  title: string;
  getAllService: () => Promise<any>;
  createService: (body: { name: string }) => Promise<any>;
  updateService: (body: { _id: string; name: string }) => Promise<any>;
  deleteService: (body: { _id: string }) => Promise<any>;
  deleteProductService: (body: { _id: string }) => Promise<any>;
  /** Products still pointing at this entity must be deleted first: their FK column is checked
   * (NOT NULL for category, nullable for type, but both cascades are handled the same way here)
   * before the entity itself can be removed, or the delete fails on the FK constraint. */
  productMatchesEntity: (product: any, entityId: string) => boolean;
}

const AdminNamedEntityList = ({
  title,
  getAllService,
  createService,
  updateService,
  deleteService,
  deleteProductService,
  productMatchesEntity,
}: AdminNamedEntityListProps) => {
  const { t } = useTranslation(["common", "error"]);
  const toast = useRef<Toast>(null);

  const [items, setItems] = useState<NamedEntity[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [newName, setNewName] = useState<string>("");
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [filters, setFilters] = useState({
    global: { value: null as string | null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [editingItem, setEditingItem] = useState<NamedEntity | null>(null);
  const [deletingItem, setDeletingItem] = useState<NamedEntity | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [affectedProductsCount, setAffectedProductsCount] = useState<number | null>(null);

  const loadItems = () => {
    setLoading(true);
    getAllService()
      .then((res: any) => setItems(res.data ?? []))
      .catch((err: any) => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onGlobalFilterChange = (value: string) => {
    setFilters({ global: { value, matchMode: FilterMatchMode.CONTAINS } });
    setGlobalFilterValue(value);
  };

  const addItem = () => {
    if (!newName.trim()) {
      return showError(toast, t("error:missingField"));
    }
    createService({ name: newName.trim() })
      .then(() => {
        setNewName("");
        loadItems();
      })
      .catch(() => showError(toast, t("common:genericError")));
  };

  const submitEdit = () => {
    if (!editingItem) return;
    updateService({ _id: editingItem._id, name: editingItem.name })
      .then(() => {
        showSuccess(toast, t("common:Save"));
        setEditingItem(null);
        loadItems();
      })
      .catch(() => showError(toast, t("common:genericError")));
  };

  const openDeleteDialog = (item: NamedEntity) => {
    setDeletingItem(item);
    setAffectedProductsCount(null);
    getAllproductsService()
      .then((res: any) => {
        const affected = (res.data ?? []).filter((product: any) =>
          productMatchesEntity(product, item._id)
        );
        setAffectedProductsCount(affected.length);
      })
      .catch((err: any) => console.error(err));
  };

  const confirmDelete = () => {
    if (!deletingItem) return;
    setDeleting(true);
    getAllproductsService()
      .then((res: any) => {
        const affected = (res.data ?? []).filter((product: any) =>
          productMatchesEntity(product, deletingItem._id)
        );
        return Promise.all(
          affected.map((product: any) => deleteProductService({ _id: product._id }))
        );
      })
      .then(() => deleteService({ _id: deletingItem._id }))
      .then(() => {
        setItems((prev) => prev.filter((i) => i._id !== deletingItem._id));
        setDeletingItem(null);
        setAffectedProductsCount(null);
      })
      .catch(() => showError(toast, t("common:genericError")))
      .finally(() => setDeleting(false));
  };

  return (
    <div className="admin-page">
      <Toast ref={toast} position="top-right" />
      <AdminPageHeader
        title={title}
        action={
          <div className="flex flex-row align-items-center" style={{ gap: "0.5rem" }}>
            <InputText
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={title}
            />
            <Button className="general-button" onClick={addItem}>
              {t("common:addProduct")}
            </Button>
          </div>
        }
      />

      <div className="admin-page-body">
        <div className="p-input-icon-left mb-3">
          <i className="pi pi-search" />
          <InputText
            value={globalFilterValue}
            onChange={(e) => onGlobalFilterChange(e.target.value)}
            placeholder="Keyword Search"
          />
        </div>

        <DataTable
          value={items}
          loading={loading}
          paginator
          rows={20}
          dataKey="_id"
          filters={filters}
          globalFilterFields={["name"]}
          sortField="name"
          sortOrder={1}
        >
          <Column field="name" header={t("common:Name")} sortable />
          <Column
            body={(row: NamedEntity) => (
              <div className="admin-row-actions">
                <Button
                  icon="pi pi-pencil"
                  className="p-button-rounded p-button-text"
                  onClick={() => setEditingItem({ ...row })}
                />
                <Button
                  icon="pi pi-trash"
                  className="p-button-rounded p-button-warning"
                  onClick={() => openDeleteDialog(row)}
                />
              </div>
            )}
          />
        </DataTable>
      </div>

      <Dialog
        header={title}
        visible={editingItem != null}
        onHide={() => setEditingItem(null)}
        footer={
          <div className="flex justify-content-space-between">
            <Button className="general-button" onClick={() => setEditingItem(null)}>
              {t("common:undo")}
            </Button>
            <Button className="general-button" onClick={submitEdit}>
              {t("common:Save")}
            </Button>
          </div>
        }
      >
        {editingItem && (
          <InputText
            style={{ width: "100%" }}
            value={editingItem.name}
            onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
          />
        )}
      </Dialog>

      <Dialog
        onHide={() => setDeletingItem(null)}
        visible={deletingItem != null}
        closable={false}
        style={{ maxWidth: "50vh" }}
        footer={
          <div className="flex justify-content-space-between">
            <Button
              className="general-button"
              onClick={() => setDeletingItem(null)}
              disabled={deleting}
            >
              {t("common:No")}
            </Button>
            <Button className="general-button" onClick={confirmDelete} disabled={deleting}>
              {t("common:Yes")}
            </Button>
          </div>
        }
      >
        <div>{deletingItem?.name}</div>
        {affectedProductsCount != null && affectedProductsCount > 0 && (
          <div className="p-error mt-2">
            {t("common:cascadeDeleteWarning", { count: affectedProductsCount })}
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default AdminNamedEntityList;
